import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Check, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { PromptTypeStep } from "./PromptTypeStep";
import { VariablesStep } from "./VariablesStep";
import { TemplateStep } from "./TemplateStep";
import { constructMessage, validateVariables } from "./messageConstructor";
import { MarketRecapWizardState, PromptType } from "../../types/market-recap";
import { getPromptConfig } from "./promptConfigs";
import { conversationAPI, templateAPI } from "../../services/api";
import { useConversationStore } from "../../stores/conversationStore";
import { useUserStore } from "../../stores/userStore";
import { useSettingsStore } from "../../stores/settingsStore";
import { toast } from "../../hooks/use-toast";

const STEPS = [
  { id: 1, name: "Recap Type", description: "Choose what to recap" },
  { id: 2, name: "Details", description: "Fill in the specifics" },
  { id: 3, name: "Template", description: "Select visual style" },
];

export function MarketRecapWizard() {
  const navigate = useNavigate();
  const { addConversation, updateConversationProject } = useConversationStore();
  const { activeOrganization, activeTeam } = useUserStore();
  const { currentModel } = useSettingsStore();

  const [wizardState, setWizardState] = useState<MarketRecapWizardState>({
    currentStep: 0,
    promptType: null,
    variables: {},
    selectedTemplateId: null,
    selectedTemplateName: null,
    selectedTemplatePreview: null,
  });

  const [isCreating, setIsCreating] = useState(false);

  // Initialize variables with defaults when prompt type changes
  const handlePromptTypeSelect = (promptType: PromptType) => {
    const config = getPromptConfig(promptType);
    const defaultVariables: Record<string, string | string[]> = {};

    if (config) {
      for (const variable of config.variables) {
        if (variable.default !== undefined) {
          defaultVariables[variable.name] = variable.default;
        }
      }
    }

    setWizardState((prev) => ({
      ...prev,
      promptType,
      variables: defaultVariables,
      currentStep: 1, // Auto-advance to next step
    }));
  };

  const handleVariableChange = (name: string, value: string | string[]) => {
    setWizardState((prev) => ({
      ...prev,
      variables: {
        ...prev.variables,
        [name]: value,
      },
    }));
  };

  const handleTemplateSelect = (
    templateId: string,
    templateName: string,
    previewUrl?: string
  ) => {
    setWizardState((prev) => ({
      ...prev,
      selectedTemplateId: templateId,
      selectedTemplateName: templateName,
      selectedTemplatePreview: previewUrl || null,
    }));
  };

  const handleBack = () => {
    setWizardState((prev) => ({
      ...prev,
      currentStep: Math.max(0, prev.currentStep - 1),
    }));
  };

  const handleNext = () => {
    // Validate current step before advancing
    if (wizardState.currentStep === 0 && !wizardState.promptType) {
      toast({
        title: "Selection Required",
        description: "Please select a recap type to continue.",
        variant: "destructive",
      });
      return;
    }

    if (wizardState.currentStep === 1) {
      const { valid, missing } = validateVariables(wizardState);
      if (!valid) {
        toast({
          title: "Missing Information",
          description: `Please fill in: ${missing.join(", ")}`,
          variant: "destructive",
        });
        return;
      }
    }

    if (wizardState.currentStep === 2 && !wizardState.selectedTemplateId) {
      toast({
        title: "Template Required",
        description: "Please select a template to continue.",
        variant: "destructive",
      });
      return;
    }

    setWizardState((prev) => ({
      ...prev,
      currentStep: Math.min(STEPS.length - 1, prev.currentStep + 1),
    }));
  };

  const handleCreate = async () => {
    if (!activeOrganization || !activeTeam) {
      toast({
        title: "Team Required",
        description: "Please select an organization and team first.",
        variant: "destructive",
      });
      return;
    }

    if (!wizardState.selectedTemplateId) {
      toast({
        title: "Template Required",
        description: "Please select a template to continue.",
        variant: "destructive",
      });
      return;
    }

    setIsCreating(true);

    try {
      const message = constructMessage(wizardState);

      // Create conversation
      const response = await conversationAPI.create({
        title: message.substring(0, 50),
        model: currentModel || "gemini-2.5-pro",
        organizationId: activeOrganization.id,
        teamId: activeTeam.id,
        collectionType: "SLIDE",
      });

      const convId = response.conversation.id;
      addConversation(response.conversation);

      // Mark as slide project
      updateConversationProject(convId, {
        hasSlideProject: true,
        projectId: convId,
      });

      // Apply template
      try {
        await templateAPI.applyTemplate(convId, wizardState.selectedTemplateId);
      } catch (templateError) {
        console.error("Failed to apply template:", templateError);
        // Don't fail the entire flow
      }

      // Navigate to slide workspace with auto-send
      await navigate({
        to: "/chat/$conversationId/slide-workspace",
        params: { conversationId: convId },
        search: { tab: undefined },
        state: {
          autoSend: true,
          userInstructions: message,
        } as any,
      });
    } catch (error) {
      console.error("Failed to create market recap:", error);
      toast({
        title: "Creation Failed",
        description:
          error instanceof Error ? error.message : "Failed to create slide",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const canProceed = () => {
    switch (wizardState.currentStep) {
      case 0:
        return !!wizardState.promptType;
      case 1:
        return validateVariables(wizardState).valid;
      case 2:
        return !!wizardState.selectedTemplateId;
      default:
        return false;
    }
  };

  const isLastStep = wizardState.currentStep === STEPS.length - 1;
  const message = constructMessage(wizardState);

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Step Indicator */}
      <div className="flex items-center justify-center gap-2">
        {STEPS.map((step, index) => (
          <div key={step.id} className="flex items-center">
            <div
              className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
                index === wizardState.currentStep
                  ? "bg-primary text-primary-foreground"
                  : index < wizardState.currentStep
                  ? "bg-primary/20 text-primary"
                  : "bg-muted text-muted-foreground"
              }`}
            >
              {index < wizardState.currentStep ? (
                <Check className="h-4 w-4" />
              ) : (
                <span className="w-4 text-center text-sm font-medium">
                  {step.id}
                </span>
              )}
              <span className="text-sm font-medium hidden sm:inline">
                {step.name}
              </span>
            </div>
            {index < STEPS.length - 1 && (
              <div
                className={`w-8 h-0.5 mx-2 ${
                  index < wizardState.currentStep ? "bg-primary" : "bg-muted"
                }`}
              />
            )}
          </div>
        ))}
      </div>

      {/* Step Content */}
      <Card className="floating-card">
        <CardContent className="p-6">
          {wizardState.currentStep === 0 && (
            <PromptTypeStep
              selectedType={wizardState.promptType}
              onSelect={handlePromptTypeSelect}
            />
          )}

          {wizardState.currentStep === 1 && (
            <VariablesStep
              promptType={wizardState.promptType!}
              variables={wizardState.variables}
              onChange={handleVariableChange}
            />
          )}

          {wizardState.currentStep === 2 && (
            <TemplateStep
              selectedTemplateId={wizardState.selectedTemplateId}
              onSelect={handleTemplateSelect}
            />
          )}
        </CardContent>
      </Card>

      {/* Message Preview (show on template step) */}
      {wizardState.currentStep === 2 && message && (
        <Card className="bg-muted/50 border-dashed">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground mb-1">
              Message to send:
            </p>
            <p className="text-foreground font-medium">{message}</p>
          </CardContent>
        </Card>
      )}

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={handleBack}
          disabled={wizardState.currentStep === 0 || isCreating}
        >
          <ChevronLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        {isLastStep ? (
          <Button
            onClick={handleCreate}
            disabled={!canProceed() || isCreating}
            className="bg-primary hover:bg-primary/90"
          >
            {isCreating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                Create Slide
                <ChevronRight className="h-4 w-4 ml-2" />
              </>
            )}
          </Button>
        ) : (
          <Button onClick={handleNext} disabled={!canProceed()}>
            Next
            <ChevronRight className="h-4 w-4 ml-2" />
          </Button>
        )}
      </div>
    </div>
  );
}
